/**
 * Pure computation of which action/status banner the caregiver `/me` page shows.
 *
 * Fix-verify (High): the "banking rejected" alert was added to the render but
 * `hasBanner` — the parent gate that decides whether the banner slot renders at
 * all — omitted `bankingRejected`, so for a caregiver whose banking was rejected
 * (and nothing else was outstanding) the whole banner was `undefined` and the
 * alert never showed. Extracting the decision here makes it unit-testable so
 * that class of gate/omission can't silently return.
 */
export interface CaregiverBannerInput {
  status: string;
  /** Tax identity complete = SIN (individual) OR Business Number (corporation)
   *  on file. Must NOT be `sinOnFile` — an incorporated caregiver has no SIN by
   *  design, so keying on SIN nags them with a permanent, unsatisfiable banner. */
  taxIdentityOnFile: boolean;
  eftSubmitted: boolean;
  eftVerified: boolean;
  eftRejected: boolean;
}

export interface CaregiverBannerFlags {
  /** SIN missing OR banking never submitted — caregiver action needed. */
  needsTaxInfo: boolean;
  /** Operator rejected the submitted void cheque — action required (re-submit). */
  bankingRejected: boolean;
  /** Submitted, not yet operator-verified — informational wait state. */
  awaitingVerification: boolean;
  /** Whether the banner slot renders at all — MUST cover every sub-state above. */
  hasBanner: boolean;
  /** Audit M2 (2026-09-01) — the STILL-MISSING onboarding items, for dynamic
   *  banner copy. The banner used to hardcode all three ("tax identity, banking
   *  details, and a void cheque") regardless of state, so a caregiver with
   *  banking verified but SIN missing was nagged to add banking they'd already
   *  submitted — training them to ignore the banner. Empty when nothing missing. */
  missingItems: string[];
}

export function caregiverBannerFlags(d: CaregiverBannerInput): CaregiverBannerFlags {
  const isActive = d.status === 'ACTIVE';
  const needsTaxInfo = isActive && (!d.taxIdentityOnFile || !d.eftSubmitted);
  // Rejected takes precedence over awaiting-verification: rejection leaves
  // eftSubmitted set, so both would otherwise be true — the caregiver must act.
  const bankingRejected = isActive && d.eftRejected;
  const awaitingVerification =
    isActive && d.eftSubmitted && !d.eftVerified && !d.eftRejected;
  const hasBanner = !isActive || needsTaxInfo || awaitingVerification || bankingRejected;
  const missingItems: string[] = [];
  if (isActive && !d.taxIdentityOnFile) missingItems.push('your tax identity (SIN or Business Number)');
  if (isActive && !d.eftSubmitted) missingItems.push('your banking details and a void cheque');
  return { needsTaxInfo, bankingRejected, awaitingVerification, hasBanner, missingItems };
}

/** Join items into "a, b, and c" (Oxford) for the banner sentence. */
export function joinAnd(items: string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}
